import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { Children, ReturnComponent, Style } from './reactTypes';

const isExternalLink = (link: string): boolean => /^\w+:/.test(link);

export const linkProps = ({
    link,
    openInNewTab,
}: {
    link: string;
    openInNewTab?: boolean | undefined;
}) => {
    if (openInNewTab === undefined) {
        openInNewTab = isExternalLink(link);
    }
    return {
        href: link,
        ...(openInNewTab && {
            target: '_blank',
            // It's a security risk to not include rel='noopener noreferrer' when opening a new tab.
            //  See https://www.jitbit.com/alexblog/256-targetblank---the-most-underestimated-vulnerability-ever/
            rel: 'noopener noreferrer',
        }),
    };
};

export const NewTabLink = ({
    to,
    children,
    style = {},
    ...rest
}: {
    to: string;
    children: Children;
    style?: Style;
}): ReturnComponent => (
    <a
        {...linkProps({ link: to, openInNewTab: true })}
        style={{ color: 'inherit', ...style }}
        {...rest}
    >
        {children}
    </a>
);

export const openInNewTab = (link: string): void => {
    window.open(link, '_blank');
};

export const useOpenLinkWithNewTabSupport = () => {
    const navigate = useNavigate();
    return useCallback(
        (link: string | undefined, event: { metaKey: boolean; ctrlKey: boolean }) => {
            if (!link) {
                return;
            }
            // metaKey is for MacOS, ctrlKey is for Windows/Linux
            if (event.metaKey || event.ctrlKey) {
                openInNewTab(link);
            } else {
                void navigate(link);
            }
        },
        [navigate],
    );
};
